import React from 'react';
import { Alert, Button, Card, Space, Tag, Typography } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, LockOutlined } from '@ant-design/icons';
import type { DeliveryGroupDraft, StopExecutionLockState } from './deliveryGroupTypes';
import { buildOrderedRouteStops } from './orderedRouteStops';
import { moveStopInGlobalItinerary } from './editorStopOrdering';
import { validateItineraryAllocationSafety } from './itineraryAllocationSafety';
import type { ItineraryCargoContext } from './itineraryStopSummary';
import { formatRouteStopPlannedAt } from './formatRouteStopPlannedAt';
import { ROUTE_UX } from './routeUxLabels';
import './routeTopologyPanel.css';

function stopLockState(stop: { lockState?: StopExecutionLockState | null }): StopExecutionLockState {
  return stop.lockState ?? 'FUTURE';
}

function lockStateBadge(lockState: StopExecutionLockState): React.ReactNode {
  if (lockState === 'COMPLETED') {
    return <Tag color="success">Точка выполнена</Tag>;
  }
  if (lockState === 'CURRENT') {
    return <Tag color="processing">{ROUTE_UX.currentStopBadge}</Tag>;
  }
  return null;
}

function lockHint(lockState: StopExecutionLockState): string | null {
  if (lockState === 'COMPLETED') {
    return 'Точка уже выполнена — изменения могут повлиять на историю исполнения';
  }
  if (lockState === 'CURRENT') {
    return 'Точка в исполнении — доступны только контактные поля';
  }
  return null;
}

const { Text, Title } = Typography;

type ApplicationRef = {
  id: number;
  internalNumber?: string | null;
  client?: { name?: string | null } | null;
};

type Props = {
  groups: DeliveryGroupDraft[];
  onChange: (next: DeliveryGroupDraft[]) => void;
  applications: ApplicationRef[];
  warehouses?: Array<{ id: number; name: string; city?: string; address?: string }>;
  consignees?: Array<{ id: number; name: string; city?: string; address?: string }>;
  cargoContext?: ItineraryCargoContext;
  compact?: boolean;
};

export function RouteItineraryPanel({
  groups,
  onChange,
  applications,
  warehouses,
  consignees,
  cargoContext,
  compact = false
}: Props) {
  const rows = React.useMemo(
    () =>
      buildOrderedRouteStops({
        groups,
        applications,
        warehouses,
        consignees,
        cargoContext,
        isStopLocked: (stop) => stopLockState(stop) !== 'FUTURE'
      }),
    [groups, applications, warehouses, consignees, cargoContext]
  );

  const safetyIssues = React.useMemo(
    () => validateItineraryAllocationSafety(groups),
    [groups]
  );

  const move = (stopUid: string, direction: -1 | 1) => {
    onChange(moveStopInGlobalItinerary(groups, stopUid, direction));
  };

  if (rows.length === 0) {
    return (
      <Card
        size="small"
        className="route-itinerary-panel"
        style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}
      >
        <Text type="secondary">Добавьте точки погрузки и выгрузки в группах маршрута.</Text>
      </Card>
    );
  }

  return (
    <Card
      size="small"
      className="route-itinerary-panel"
      title={
        <Title level={5} style={{ margin: 0 }}>
          {ROUTE_UX.itinerarySection}
        </Title>
      }
      style={{ background: '#fafcff', borderColor: '#91caff' }}
    >
      {!compact ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {ROUTE_UX.itineraryHint}
        </Text>
      ) : null}

      {safetyIssues.length > 0 ? (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
          {safetyIssues.map((issue) => (
            <Alert key={`${issue.code}-${issue.stopUid ?? issue.cargoId}`} type="error" showIcon message={issue.message} />
          ))}
        </Space>
      ) : null}

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {rows.map((row, index) => (
          <div
            key={row.stopUid}
            className={`route-itinerary-panel__row ${row.locked ? 'route-itinerary-panel__row--locked' : 'route-itinerary-panel__row--future'}`}
          >
            <div style={{ minWidth: 28, fontWeight: 600, color: '#1677ff' }}>#{row.position + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="route-itinerary-panel__meta" style={{ marginBottom: 4 }}>
                <Text strong>{row.stopName}</Text>
                {lockStateBadge(row.lockState)}
                {row.locked ? (
                  <LockOutlined style={{ color: '#faad14' }} title={lockHint(row.lockState) || ROUTE_UX.lockedStopHint} />
                ) : null}
              </div>
              <div>
                <Text type="secondary">{row.pointTypeLabel}</Text>
                {row.pointLabel ? <Text type="secondary"> · {row.pointLabel}</Text> : null}
              </div>
              {row.fullAddress ? (
                <div>
                  <Text>{row.fullAddress}</Text>
                </div>
              ) : null}
              <div>
                <Text type="secondary">
                  Грузов: {row.cargoCount}
                  {row.plannedAt ? ` · ${ROUTE_UX.date}: ${formatRouteStopPlannedAt(row.plannedAt)}` : ''}
                </Text>
              </div>
              {!compact ? (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.groupLabel}
                    {row.applicationLabel ? ` · заявка ${row.applicationLabel}` : ''}
                  </Text>
                </div>
              ) : null}
            </div>
            <Space direction="vertical" size={4}>
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index === 0 || row.locked}
                onClick={() => move(row.stopUid, -1)}
                aria-label="Выше"
              />
              <Button
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index === rows.length - 1 || row.locked}
                onClick={() => move(row.stopUid, 1)}
                aria-label="Ниже"
              />
            </Space>
          </div>
        ))}
      </Space>
    </Card>
  );
}
